import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppLayout from "@/components/AppLayout";
import { MessagesSquare, Plus, MessageCircle, ShieldCheck, Clock3, X } from "lucide-react";

function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function Community() {
  const { user } = useAuth();
  const variant = user?.role === "admin" ? "admin" : "student";
  const basePath = variant === "admin" ? "/admin/community" : "/student/community";

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [myPending, setMyPending] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/community/threads");
      setThreads(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error("Please add a title and message");
      return;
    }
    setPosting(true);
    try {
      const { data } = await api.post("/community/threads", { title, body });
      const s = data?.post?.status;
      if (s === "approved") {
        toast.success("Posted!");
        setShowNew(false);
        setTitle(""); setBody("");
        load();
      } else if (s === "pending") {
        toast.success("Your post is awaiting admin approval");
        setMyPending(data.post);
        setShowNew(false);
        setTitle(""); setBody("");
      } else if (s === "rejected") {
        toast.error("Your post was blocked: please keep it friendly and kid-safe");
        setShowNew(false);
        setTitle(""); setBody("");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <AppLayout variant={variant}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-light text-sky-hover font-bold text-sm mb-3">
              <MessagesSquare className="w-4 h-4" /> Community
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Discussions</h1>
            <p className="text-lg text-stone mt-2">Ask questions, share progress, and cheer on classmates.</p>
          </div>
          <div className="flex gap-2">
            {variant === "admin" && (
              <Link
                to="/admin/community/moderation"
                data-testid="link-moderation"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lemon text-ink font-bold text-sm btn-tactile hover:brightness-95"
              >
                <ShieldCheck className="w-4 h-4" /> Moderation
              </Link>
            )}
            <button
              data-testid="new-thread-button"
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-watermelon text-white font-bold text-sm btn-tactile hover:bg-watermelon-hover"
            >
              <Plus className="w-4 h-4" /> New discussion
            </button>
          </div>
        </div>

        {myPending && (
          <div data-testid="pending-banner" className="mb-6 p-4 rounded-3xl bg-lemon/40 border-2 border-lemon flex items-start gap-3">
            <Clock3 className="w-5 h-5 text-ink mt-0.5" />
            <div className="flex-1">
              <div className="font-heading font-bold text-ink">Waiting for admin approval</div>
              <div className="text-sm text-stone">"{myPending.title}" will appear once a teacher approves it.</div>
            </div>
            <button onClick={() => setMyPending(null)} className="text-stone hover:text-ink"><X className="w-5 h-5" /></button>
          </div>
        )}

        {loading ? (
          <div data-testid="threads-loading" className="text-stone">Loading discussions…</div>
        ) : threads.length === 0 ? (
          <div data-testid="threads-empty" className="p-10 bg-white rounded-3xl card-tactile text-center">
            <MessageCircle className="w-10 h-10 text-sky mx-auto mb-3" />
            <div className="font-heading font-bold text-xl text-ink mb-1">No discussions yet</div>
            <div className="text-stone">Be the first to start a conversation.</div>
          </div>
        ) : (
          <ul data-testid="threads-list" className="space-y-3">
            {threads.map((t) => (
              <li key={t.id}>
                <Link
                  to={`${basePath}/${t.id}`}
                  data-testid={`thread-card-${t.id}`}
                  className="block p-5 bg-white rounded-3xl card-tactile hover:-translate-y-0.5 transition-transform"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                          t.author_role === "admin" ? "bg-sky text-white" : "bg-canvas text-stone"
                        }`}>
                          {t.author_role === "admin" ? "Teacher" : "Student"}
                        </span>
                        <span className="text-sm font-semibold text-ink truncate">{t.author_name}</span>
                        <span className="text-xs text-pebble">·</span>
                        <span className="text-xs text-pebble">{fmtRelative(t.created_at)}</span>
                      </div>
                      <h3 className="font-heading text-xl font-bold text-ink leading-snug truncate">{t.title}</h3>
                      <p className="text-sm text-stone mt-1 line-clamp-2">{t.body}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-light text-sky-hover text-xs font-bold">
                        <MessageCircle className="w-3.5 h-3.5" /> {t.reply_count}
                      </div>
                      <div className="text-xs text-pebble mt-1">Last: {fmtRelative(t.last_activity_at)}</div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm overflow-y-auto" onClick={() => !posting && setShowNew(false)}>
          <div className="min-h-full flex items-end sm:items-center justify-center p-4">
            <div
              data-testid="new-thread-modal"
              className="w-full max-w-lg bg-white rounded-3xl p-6 shadow-2xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-bold text-ink">Start a discussion</h2>
              <button onClick={() => setShowNew(false)} disabled={posting} className="text-stone hover:text-ink">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-ink mb-1">Title</label>
                <input
                  data-testid="thread-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={140}
                  placeholder="What's on your mind?"
                  className="w-full h-11 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-semibold"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-ink mb-1">Message</label>
                <textarea
                  data-testid="thread-body-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={4000}
                  rows={6}
                  placeholder="Share your question or thought…"
                  className="w-full px-4 py-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none resize-none"
                />
              </div>
              {variant === "student" && (
                <div className="text-xs text-stone bg-canvas rounded-2xl p-3">
                  Please keep it friendly. A teacher will review your post before it appears.
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowNew(false)} disabled={posting} className="px-4 py-2 rounded-full text-stone font-bold">Cancel</button>
                <button
                  type="submit"
                  data-testid="thread-submit-button"
                  disabled={posting}
                  className="px-5 py-2 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
                >
                  {posting ? "Posting…" : "Post"}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
